import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, ForwardRefRenderFunction } from "react";
import { IBrandList, IkindList, IModalList } from "./types";
import { getBrandsAtom, getKindsAtom, getModalsAtom } from "./dataStructure";
import { useAtom } from "jotai";
import BrandKindSelect from "./brandKindSelect";
import { useSetState, useUpdateEffect } from "ahooks";
import { IBrandSelectData } from "./types";
import ModalPopoverWrapper from "./modalSelect";
import styles from "./index.module.less";

interface IAction {
  clear: () => void;
  select: (data: any) => void;
}

interface IProps {
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  size?: "small" | "middle" | "large";
  border?: boolean;
  error?: boolean;
  ref?: any;
  /** @description brandKindModel 可选择三级 | brandKind 只可选择两级 | brand 僅可選擇廠牌 */
  selectType?: "brandKindModel" | "brandKind" | "brand";
  /** @description 是否开启选择全部车款/全部车型 */
  isSelectAll?: boolean;
  onSubmit: (data: Partial<IBrandSelectData> | { modelYearId: string; modelYearName: string }) => void;
  /** @description 隐藏车型选择 */
  hideModal?: boolean;
  /**
   * @description 车型年份类型
   * @description valuation：车款过滤掉无估价的车款，车型需要隐藏选择2008年以前
   */
  modelYearType?: "valuation" | "valuation2";
}

const BrandsKindSelectDom: ForwardRefRenderFunction<IAction, IProps> = (
  {
    disabled,
    size = "middle",
    selectType,
    onSubmit,
    error,
    placeholder,
    isSelectAll = false,
    className,
    border,
    hideModal = false,
    modelYearType = "",
  },
  propRef,
) => {
  const [brandData, getBrands] = useAtom(getBrandsAtom);
  const [kindData, getkinds] = useAtom(getKindsAtom);
  const [modalData, getModals] = useAtom(getModalsAtom);
  const [isShowClear, setShowClear] = useState<boolean>(false);
  const [params, setParams] = useSetState<IBrandSelectData>({
    brandId: undefined,
    brandName: "",
    kindId: undefined,
    kindName: "",
    modelYearId: "",
    modelYearName: "",
  });
  const [defaultModelYear, setDefaultModelYear] = useState<any>({
    modelYearId: "",
    modelYearName: "",
  });
  const ref = useRef<any>(null);

  const handleSubmit = async (data: Partial<IBrandSelectData>, type: "kind" | "model" | "all" | "modelYear") => {
    if (type === "all") {
      setParams((prev) => ({
        ...prev,
        ...data,
      }));
      onSubmit({ ...data });
      return;
    }

    if (type === "kind") {
      setParams((prev) => ({
        ...prev,
        ...data,
      }));
      getModals({
        kindId: data.kindId ?? "",
        type: modelYearType,
      });
      setDefaultModelYear({
        modelYearId: "",
        modelYearName: "",
      });
      onSubmit({ ...params, ...data, kindId: data.kindId, modelYearId: "", modelYearName: "", brandName: params.brandName });
    }

    if (type === "modelYear") {
      setParams((prev) => ({
        ...prev,
        ...data,
      }));
      onSubmit({ ...params, ...data, kindId: data.kindId, modelYearId: data.modelYearId, modelYearName: data.modelYearName });
    }
  };

  const handleBrandSelect = (brandId, brandName) => {
    setParams({
      brandId,
      brandName,
    });

    if (selectType !== "brand") {
      getkinds({ brandId, type: modelYearType });
    } else {
      onSubmit({ ...params, brandId, brandName });
    }
  };

  const handleKindSelect = (kindId) => {
    setParams({
      kindId,
    });
    getModals({ kindId, type: modelYearType });
  };

  const initModelYear = async (kindId) => {
    getModals({ kindId, type: modelYearType });
  };

  useEffect(() => {
    getBrands({ type: modelYearType });
  }, []);

  useImperativeHandle(propRef, () => ({
    clear() {
      ref.current?.clear();
    },
    select(data: any) {
      if (data?.kindId) {
        initModelYear(data.kindId);
      }

      if (!!data?.modelYearId || data?.modelYearId === 0) {
        setDefaultModelYear({
          modelYearId: data.modelYearId,
          modelYearName: data.modelYearName,
        });
      }

      ref.current?.select(data);
    },
  }));

  const resetLayer = () => {
    ref.current?.resetLayer();
  }

  return (
    <div className={styles["brand-kind-select"]}>
      <BrandKindSelect
        hideModal={hideModal}
        disabled={disabled}
        size={size}
        border={border}
        className={className}
        error={error}
        ref={(childrenRef) => {
          ref.current = childrenRef;
        }}
        selectType={selectType}
        isSelectAll={isSelectAll}
        field={"newcar"}
        selectLabel={placeholder ?? "請選擇"}
        dataSource={brandData}
        kindData={kindData}
        modelData={modalData}
        onBrandSelect={handleBrandSelect}
        onKindSelect={handleKindSelect}
        modelYearType={modelYearType}
        onClear={() => {
          let initOjb = {
            brandId: undefined,
            brandName: "",
            kindId: undefined,
            kindName: "",
            modelYearId: "",
            modelYearName: "",
          };
          setParams({ ...initOjb });
          onSubmit({ ...initOjb });
          setDefaultModelYear({
            modelYearId: "",
            modelYearName: "",
          })
          setShowClear(false);
          resetLayer()
        }}
        isShowClear={isShowClear}
        onSubmit={handleSubmit}
        modelYearData={modalData}
        modelYearId={defaultModelYear.modelYearId}
        modelYearName={defaultModelYear.modelYearName}
      />
    </div>
  );
};

export default forwardRef(BrandsKindSelectDom);
